/**
 * Goods receipt.
 *
 * Phase 0 finding F8 said this is the first screen Phase 1 should ship: the
 * flyash purchase register was blank for the whole of June 2026, so 78,937 kg
 * of washing output had no recorded input at all. Nothing downstream can be
 * accounted for until material has a provenance.
 */

import { useEffect, useMemo, useState } from "react";
import { api, ApiError, OfflineQueued } from "../../lib/api";
import type { Item } from "../../lib/types";
import { fmtINR, fmtQty, today } from "../../lib/format";

interface Row {
  key: string;
  itemId: number | "";
  qty: string;
  rate: string;
}

const blank = (): Row => ({
  key: Math.random().toString(36).slice(2),
  itemId: "",
  qty: "",
  rate: "",
});

export function PurchaseEntry() {
  const [items, setItems] = useState<Item[]>([]);
  const [businessDate, setBusinessDate] = useState(() =>
    today()
  );
  const [locationCode, setLocationCode] = useState("STORE_SCRAP");
  const [docRef, setDocRef] = useState("");
  const [remarks, setRemarks] = useState("");
  const [rows, setRows] = useState<Row[]>([blank(), blank()]);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  useEffect(() => {
    api.items().then(setItems).catch(() => undefined);
  }, []);

  const total = useMemo(
    () =>
      rows.reduce((sum, r) => {
        const q = Number(r.qty);
        const rate = Number(r.rate);
        return sum + (Number.isFinite(q) && Number.isFinite(rate) ? q * rate : 0);
      }, 0),
    [rows]
  );

  function update(index: number, patch: Partial<Row>) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      if (index === next.length - 1) next.push(blank());
      return next;
    });
  }

  async function submit() {
    const lines = rows
      .filter((r) => r.itemId !== "" && Number(r.qty) > 0)
      .map((r) => ({
        item_id: Number(r.itemId),
        qty: Number(r.qty),
        rate: r.rate ? Number(r.rate) : null,
      }));

    if (!lines.length) {
      setBanner({ tone: "warning", text: "Add at least one line." });
      return;
    }

    setBusy(true);
    setBanner(null);
    try {
      await api.createPurchase({
        business_date: businessDate,
        location_code: locationCode,
        doc_ref: docRef || null,
        remarks: remarks || null,
        lines,
      });
      setBanner({
        tone: "positive",
        text: `${lines.length} line${lines.length === 1 ? "" : "s"} received into stock.`,
      });
      setRows([blank(), blank()]);
      setDocRef("");
      setRemarks("");
    } catch (err) {
      if (err instanceof OfflineQueued) {
        setBanner({
          tone: "warning",
          text: "No connection. Saved on this device and it will sync automatically.",
        });
        return;
      }
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Goods receipt</h2>
          <p className="page__sub">
            Scrap and raw material in. Every kilogram downstream traces back here.
          </p>
        </div>
        <div className="page__controls">
          <label className="inputgroup">
            <span className="label">Into</span>
            <select
              value={locationCode}
              onChange={(e) => setLocationCode(e.target.value)}
            >
              <option value="STORE_SCRAP">Scrap Yard</option>
              <option value="STORE_RM">Raw Material Store</option>
              <option value="STORE_RP">RP Store</option>
            </select>
          </label>
          <label className="inputgroup">
            <span className="label">Date</span>
            <input
              type="date"
              value={businessDate}
              onChange={(e) => setBusinessDate(e.target.value)}
            />
          </label>
          <label className="inputgroup">
            <span className="label">Document ref</span>
            <input
              value={docRef}
              onChange={(e) => setDocRef(e.target.value)}
              placeholder="GRN/001"
            />
          </label>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      <div className="tablewrap">
        <table className="table table--entry">
          <thead>
            <tr>
              <th>Item</th>
              <th className="num">Quantity</th>
              <th className="num">Rate</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const item = items.find((i) => i.id === row.itemId);
              const value = Number(row.qty) * Number(row.rate);
              return (
                <tr key={row.key}>
                  <td>
                    <select
                      value={row.itemId}
                      onChange={(e) =>
                        update(index, {
                          itemId: e.target.value === "" ? "" : Number(e.target.value),
                        })
                      }
                    >
                      <option value="">Choose an item…</option>
                      {items.map((i) => (
                        <option key={i.id} value={i.id}>
                          {i.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input
                      className="num mono"
                      inputMode="decimal"
                      value={row.qty}
                      placeholder="0"
                      onChange={(e) => update(index, { qty: e.target.value })}
                    />
                    {item && <span className="cell__unit">{item.uom}</span>}
                  </td>
                  <td>
                    <input
                      className="num mono"
                      inputMode="decimal"
                      value={row.rate}
                      placeholder="0.00"
                      onChange={(e) => update(index, { rate: e.target.value })}
                    />
                  </td>
                  <td className="num mono">
                    {Number.isFinite(value) && value > 0
                      ? fmtINR(value, { abbreviate: false })
                      : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="num">
                Total
              </td>
              <td className="num mono" title={fmtQty(total, 2)}>
                {fmtINR(total, { abbreviate: false })}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="page__footer">
        <label className="inputgroup inputgroup--grow">
          <span className="label">Remarks</span>
          <input
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Vehicle number, supplier challan, anything worth recording"
          />
        </label>
        <button className="btn btn--auto" onClick={submit} disabled={busy}>
          {busy ? <span className="spinner" /> : null}
          {busy ? "Saving" : "Receive into stock"}
        </button>
      </div>

      {/* The rate is stored on the movement, not looked up later (A15), so a
          later revision can never restate this receipt. */}
      <p className="page__note">
        The rate entered here is frozen onto the transaction. Changing a rate
        master later will not restate this receipt.
      </p>
    </div>
  );
}
